import { FLAG_TYPE_ERROR, FLAG_TYPE_INFO, FLAG_TYPE_SUCCESS } from '@atlassian/mpac-types';
import { UrlUtils } from '@atlassian/mpac-utils';
import { AppInstallationPartnerErrorCodeOwnership } from '../../constants/AppInstallationErrorCodes';
import { getErrorDetails } from '../../utils/request-utils';
import { ERROR_CODE } from './hooks/useMarketplaceAppInstall/constants';
export const appInstallFlagKeys = {
  inProgress: appIdentifier => "".concat(appIdentifier, "-app-install-in-progress"),
  installSuccess: appIdentifier => "".concat(appIdentifier, "-app-install-success"),
  installError: appIdentifier => "".concat(appIdentifier, "-app-install-error"),
  noSimultaneousInstalls: () => 'no-simultaneous-installs-allowed'
};
export function getInstallInProgressFlagProps(appIdentifier, appName, onDismiss) {
  return {
    id: appInstallFlagKeys.inProgress(appIdentifier),
    type: FLAG_TYPE_INFO,
    title: "Adding ".concat(appName, "..."),
    description: "We'll tell you when your app has been added.",
    isAutoDismiss: false,
    actions: [{
      content: 'Close',
      onClick: () => onDismiss(appInstallFlagKeys.inProgress(appIdentifier))
    }]
  };
}
export const INSTALL_SUCCESS_ACTION_CONTENT = Object.freeze({
  getStarted: 'Get started',
  configure: 'Configure',
  manageApp: 'Manage app'
});
export function getInstallSuccessFlagProps(appIdentifier, appName, installedApp) {
  return {
    id: appInstallFlagKeys.installSuccess(appIdentifier),
    type: FLAG_TYPE_SUCCESS,
    title: "".concat(appName, " installed successfully"),
    description: 'Your app has been added to your instance and is ready for use.',
    isAutoDismiss: false,
    actions: [{
      content: INSTALL_SUCCESS_ACTION_CONTENT.getStarted,
      href: installedApp.getStartedUrl,
      target: '_blank'
    }, {
      content: INSTALL_SUCCESS_ACTION_CONTENT.configure,
      href: installedApp.configureUrl,
      target: '_blank'
    }, {
      content: INSTALL_SUCCESS_ACTION_CONTENT.manageApp,
      href: installedApp.manageAppUrl,
      target: '_blank'
    }].filter(action => !!action.href)
  };
}
export function getInstallErrorFlagProps(_ref) {
  let {
    appIdentifier,
    hostConfig,
    installError,
    featureFlags,
    appName,
    onDismiss,
    vendorSupportTicketUrl
  } = _ref;
  const {
    showCustomBackendErrorsFromUpm
  } = featureFlags;

  /**
   * Some of the UPM errors mappings are defined by 'message' ex: 'PaymentFailure'
   * while some of them through 'code' ex: 'connect.install.error.nodefaultgroup'.
   * These error mappings are in the hostConfig file.
   *
   * Some others which are not static are defined as separate functions:
   * ex: getCustomErrorDetails or getPartnerErrorDetails
   *
   * These should be unified through a single flow & the mappings should ideally be through 'code'
   * but should still have access to 'message' to handle complex usecases.
   */
  let errorDetails;
  if (AppInstallationPartnerErrorCodeOwnership[installError.code]) {
    errorDetails = getPartnerErrorDetails(appName, onDismiss, vendorSupportTicketUrl);
  } else {
    errorDetails = getCustomErrorDetails({
      errorCode: installError.code,
      errorMessage: installError.message,
      appName,
      onDismiss
    });
  }
  if (!errorDetails) {
    var _hostConfig$errors$ge, _errorDetails$transla;
    errorDetails = getErrorDetails(installError.message, hostConfig, {
      showCustomBackendErrorsFromUpm
    });
    if (errorDetails.translation.description === ((_hostConfig$errors$ge = hostConfig.errors.get('default')) === null || _hostConfig$errors$ge === void 0 ? void 0 : _hostConfig$errors$ge.description)) {
      errorDetails = getErrorDetails(installError.code, hostConfig, {
        showCustomBackendErrorsFromUpm
      });
    }
    if (((_errorDetails$transla = errorDetails.translation.actions) === null || _errorDetails$transla === void 0 ? void 0 : _errorDetails$transla.length) > 0) {
      errorDetails.actions = errorDetails.translation.actions;
    } else if (onDismiss) {
      errorDetails.actions = [{
        content: 'Got it',
        onClick: onDismiss
      }];
    }
  }
  return {
    id: appInstallFlagKeys.installError(appIdentifier),
    type: FLAG_TYPE_ERROR,
    title: errorDetails.title || errorDetails.translation.title,
    description: errorDetails.description || errorDetails.translation.description,
    isAutoDismiss: false,
    actions: errorDetails.actions
  };
}
function getConflictingAppNameFromErrorMessage() {
  let errorMessage = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  // Expected errorMessage format - appKey for the app we're trying to install,appKey of the Conflicting app:Conflicting App Name:macro key
  if (typeof errorMessage !== 'string') {
    return null;
  }
  const splitOnComma = errorMessage.split(',');
  if (splitOnComma.length < 2) {
    return null;
  }
  const splitOnColon = splitOnComma[1].split(':');
  if (splitOnColon.length < 2) {
    return null;
  }
  return splitOnColon[1].trim();
}

// eslint-disable-next-line max-lines-per-function, complexity
export const getCustomErrorDetails = _ref2 => {
  let {
    errorCode,
    errorMessage,
    appName,
    onDismiss
  } = _ref2;
  switch (errorCode) {
    case ERROR_CODE.FETCH_ADDON_INTERNAL_ERROR:
      return {
        title: "Fetching details for ".concat(appName),
        description: 'Your app installation is in progress. It may take some time to initiate the subscription. We’ll update you once it’s done.',
        actions: [{
          content: 'Got it',
          onClick: onDismiss
        }]
      };
    case ERROR_CODE.TICKET:
      {
        const ticketNumber = UrlUtils.getSupportTicketNumber(errorMessage);
        return {
          title: 'Sorry, installation failed',
          description: "We are unable to process your app request. A support ticket ".concat(ticketNumber, " has been created for you. We are working on it to resolve the issue at the earliest")
        };
      }
    case ERROR_CODE.APP_REACTIVATION_NOT_SUPPORTED:
      return {
        title: "Unable to install ".concat(appName),
        description: 'Unfortunately, we’re unable to process your request. Please contact support to install the app.',
        actions: [{
          content: 'Contact support',
          href: 'https://www.atlassian.com/company/contact/purchasing-licensing#/',
          target: '_blank'
        }]
      };
    case ERROR_CODE.UPM_PLUGIN_ERROR_TIMEOUT_LICENSE_CHANGE:
    case ERROR_CODE.ECOSYSTEM_ACCOUNT_PENDING_RENEWAL:
    case ERROR_CODE.CONNECT_PAYMENT_FAILURE_EXCEPTION:
      return {
        title: "Unable to install ".concat(appName),
        description: 'Please reconfirm your payment details and license status, and try again to install the app.',
        actions: [{
          content: 'Got it',
          onClick: onDismiss
        }]
      };
    case ERROR_CODE.CONNECT_INSTALL_ERROR_CONFLICTING_MACRO_ALIAS:
      return {
        title: "Unable to install ".concat(appName),
        description: "Unfortunately, we\u2019re unable to process your request due to a technical error.",
        actions: [{
          content: 'Got it',
          onClick: onDismiss
        }]
      };
    case ERROR_CODE.CONNECT_INSTALL_ERROR_DEPENDENCIES_FAILED:
      return {
        title: "Unable to install ".concat(appName),
        description: 'Unfortunately, we’re not able to process your request due to an unexpected error. Please retry again in some time.',
        actions: [{
          content: 'Got it',
          onClick: onDismiss
        }]
      };
    case ERROR_CODE.ECOSYSTEM_APP_DEACTIVATED:
    case ERROR_CODE.UPM_INSTALL_POST_ERROR:
      return {
        title: "Unable to install ".concat(appName),
        description: 'Unfortunately, we’re not able to process your request due to an unexpected error. Please retry again in some time.',
        actions: [{
          content: 'Got it',
          onClick: onDismiss
        }]
      };
    case ERROR_CODE.ECOSYSTEM_APP_INSTALL_INPROGRESS:
    case ERROR_CODE.CONNECT_INSTALL_ERROR_LOCKED:
      return {
        title: "Unable to install ".concat(appName),
        description: 'An installation is already in progress, and we cannot initiate a new one. Please try again in some time.',
        actions: [{
          content: 'Got it',
          onClick: onDismiss
        }]
      };
    case ERROR_CODE.CONNECT_DUPLICATE_MACRO_KEYS_EXCEPTION:
      {
        return {
          title: "Unable to install ".concat(appName),
          description: "Your instance has ".concat(getConflictingAppNameFromErrorMessage(errorMessage) || 'an app', " from the same partner that does not allow installation of ").concat(appName, " due to technical constraints."),
          actions: [{
            content: 'Learn more about the issue',
            href: 'https://support.atlassian.com/confluence-cloud/docs/what-are-macros/#Conflicts-between-two-macros',
            target: '_blank'
          }]
        };
      }
  }
};
const getPartnerErrorDetails = (appName, onDismiss, vendorSupportTicketUrl) => {
  if (vendorSupportTicketUrl) {
    return {
      title: "Unable to install ".concat(appName),
      description: 'Unfortunately, we’re facing a technical issue with the partner. You can contact the partner to know more about the issue.',
      actions: [{
        content: 'Contact partner',
        href: vendorSupportTicketUrl,
        target: '_blank'
      }]
    };
  } else {
    return {
      title: "Unable to install ".concat(appName),
      description: 'Unfortunately, we’re facing a technical issue with the partner. Please try again in some time.',
      actions: [{
        content: 'Got it',
        onClick: onDismiss
      }]
    };
  }
};
export function getSimultaneousInstallErrorFlagProps() {
  return {
    id: appInstallFlagKeys.noSimultaneousInstalls(),
    type: FLAG_TYPE_INFO,
    title: 'Hold on!',
    description: 'Another installation is still in progress. Try again when it is complete.',
    isAutoDismiss: true
  };
}
export function getCCPBillingSystemErrorFlag(appIdentifier) {
  return {
    id: appInstallFlagKeys.installError(appIdentifier),
    type: FLAG_TYPE_ERROR,
    title: 'App installation failed',
    description: 'We are unable to process your request for app installation. Please contact the support team to understand the issue.',
    isAutoDismiss: false,
    actions: [{
      content: 'Contact support',
      href: 'https://www.atlassian.com/company/contact/purchasing-licensing',
      target: '_blank'
    }]
  };
}